/**
 * PurchaseInvoiceDetail.tsx — silnik formatek (keystone unifikacja-formatek,
 * Krok 2 — faktury na descent + wspólne hooki pilota).
 *
 * Jeden komponent dla 3 ścieżek:
 *   - /purchase-invoices/new → tryb create (form pusty + edit mode od razu)
 *   - /purchase-invoices/:id → tryb view (read-only, button Edytuj)
 *   - /purchase-invoices/:id + Edytuj → tryb edit (PATCH, button Zapisz/Anuluj)
 *
 * Layout (silnik formatek — descent, mirror pilota commercial/internal):
 *   header (system_number/numer faktury + status badge + Edit/Save/Cancel/Delete);
 *   badge typu dokumentu — TYLKO w create (numeracja per typ LOCKED, poza silnikiem);
 *   JEDEN <RecursiveFormRenderer> na layoucie po `topUpDocumentLayout` (syntetyzuje
 *   'data' z pól core gdy brak aktywnego layoutu → gałąź `!layoutConfig` usunięta).
 *   `renderField` z `useKartotekaRenderField(purchaseInvoiceEngineConfig, …)` —
 *   core przez istniejące renderery pi-field-*, ext przez ExtFieldValueInput; pole
 *   dodatkowe pokazuje się tylko tam, gdzie ktoś postawił je w układzie jako zwykłe
 *   pole (top-up niczego nie dokleja). Tabele
 *   dodatkowe (additional_view) buforowane; 'lines'/'accounting'/'documents' —
 *   placeholdery (tabRenderers); 'audit_log' to przenośny blok
 *   (purchase_invoice_audit_view), zakładki „Pola dodatkowe" już nie ma.
 *   JEDEN fetch layoutu na stronie (useSystemModuleActiveLayout) — dawny podwójny
 *   fetch znikł razem z renderowaniem PurchaseInvoiceDataTab.
 *
 * Save flow (atomic, Krok 5 — saveAggregate):
 *   - create: POST /purchase-invoices z pełnym body (pola faktury + extValues +
 *     additionalTables, gdy niepuste — WŁĄCZNIE z wierszami dodanymi przed
 *     pierwszym zapisem) → jeden zapis w jednej transakcji.
 *   - edit:   PATCH /purchase-invoices/:id analogicznie — jedno wywołanie,
 *     bez osobnego PUT na ext-fields (usunięty; ext-fields i delty tabel
 *     dodatkowych jadą inline w body POST/PATCH, patrz saveAggregate).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UUID_REGEX } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  ArrowLeft,
  Pencil,
  Plus,
  Receipt,
  RotateCcw,
  Save,
  Trash2,
  X as XIcon,
} from 'lucide-react';

import { buildDocumentPreviewContextRenderer } from '@/components/attachments/DocumentPreviewContextRenderer';
import { DocumentPreviewDockHost } from '@/components/attachments/DocumentPreviewDockHost';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  DecreeLinesEditor,
  type SymfoniaTypeContext,
} from '@/components/documents/DecreeLinesEditor';
import { DecreeLinesSection } from '@/components/documents/DecreeLinesSection';
import { DecreeProcedureRunControls } from '@/components/documents/DecreeProcedureRunControls';
import { DecreeSuggestControls } from '@/components/documents/DecreeSuggestControls';
import { DocumentItemsEditor } from '@/components/documents/DocumentItemsEditor';
import { DocumentItemsSection } from '@/components/documents/DocumentItemsSection';
import { DocumentLayoutPicker } from '@/components/documents/DocumentLayoutPicker';
import { DocumentTypeChangeBadge } from '@/components/documents/DocumentTypeChangeBadge';
import { DocumentTypeCreateBadge } from '@/components/documents/DocumentTypeCreateBadge';
import { DocumentVatRegisterEditor } from '@/components/documents/DocumentVatRegisterEditor';
import { DocumentVatRegisterSection } from '@/components/documents/DocumentVatRegisterSection';
import {
  type TopUpContext,
  topUpDocumentLayout,
} from '@/components/documents/engine/topUpDocumentLayout';
import {
  SendToSymfoniaButton,
  SymfoniaSentStatus,
} from '@/components/documents/SendToSymfoniaButton';
import { RecursiveFormRenderer } from '@/components/form/RecursiveFormRenderer';
import { SaveFirstNotice } from '@/components/form/SaveFirstNotice';
import { IconButton } from '@/components/IconButton';
import { purchaseInvoiceEngineConfig } from '@/components/invoices/invoiceEngineConfig';
import { deriveProcessReadonlyExtra } from '@/components/invoices/processReadonlyRenderer';
import {
  buildCoreFieldRecordValues,
  collectEngineFieldErrors,
  isSlugMatch,
} from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { InvoiceAttachmentsSection } from '@/components/ksef/InvoiceAttachmentsSection';
import { ModuleGuard } from '@/components/ModuleGuard';
import { ProcessApprovalBanner } from '@/components/processes/ProcessApprovalBanner';
import {
  EMPTY_PURCHASE_INVOICE_FORM,
  type PurchaseInvoiceFormData,
} from '@/components/purchase-invoices/purchaseInvoiceFormTypes';
import { SourceBadge } from '@/components/purchase-invoices/SourceBadge';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  invalidateRecordInstances,
  recordInstancesKey,
  selectRecordInstanceRows,
} from '@/hooks/useActiveInstanceForRecord';
import { useAdditionalTableDeltas } from '@/hooks/useAdditionalTableDeltas';
import { useControlWarning } from '@/hooks/useControlWarning';
import { useDocumentDetailTab } from '@/hooks/useDocumentDetailTab';
import { useDocumentSqlButton } from '@/hooks/useDocumentSqlButton';
import { useDocumentTypeAndLayoutParams } from '@/hooks/useDocumentTypeAndLayoutParams';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import {
  applyCoreDefaults,
  numberAsString,
  useNewRecordDefaults,
} from '@/hooks/useNewRecordDefaults';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { buildAdditionalViewRecordValues } from '@/lib/additional-views/record-values';
import { api } from '@/lib/api';
import {
  decreeDraftLineToPayload,
  type DecreeLine,
  type DecreeLineDraft,
  serverDecreeLineToDraft,
} from '@/lib/documents/decree-lines';
import {
  type DocumentLineItemDraft,
  type DocumentLineItemsFields,
} from '@/lib/documents/document-items';
import {
  computeTotalsPreview,
  draftToPayload,
  serverItemToDraft,
  validateItemsDraft,
} from '@/lib/documents/document-items-draft';
import {
  type DocumentVatRegisterRow,
  type DocumentVatRegisterRowDraft,
  serverVatRegisterRowToDraft,
  vatRegisterDraftToPayload,
} from '@/lib/documents/document-vat-register';
import { invoiceStatusLabel } from '@/lib/documents/invoice-select-labels';
import { PERIOD_DATE_FIELD, periodDateValidationToast } from '@/lib/documents/period-date';
import { useNumberingBaseFieldSlug } from '@/lib/documents/useNumberingBaseFieldSlug';
import { useSymfoniaPeriodDateDraft } from '@/lib/documents/useSymfoniaPeriodDateDraft';
import { extractErrorCode } from '@/lib/errors';
import { type ProcessWarning, processWarningMessage } from '@/lib/processes/approvals-api';
import { fetchInstancesForRecord } from '@/lib/processes/instance-api';
import { DOCUMENTS_MODULE_CODE } from '@/lib/processes/module-routes';
import { mapDictionarySaveError } from '@/lib/records/save-error-message';
import { useMyModules } from '@/lib/use-my-modules';
import {
  buildExtFieldServerMessages,
  buildFieldIssueMessages,
  buildMissingFieldsDescription,
  buildValidationToastMessage,
  resolveFieldLabel,
} from '@/lib/validation-toast';

import { buildDocumentAdditionalRenderers } from './document-additional-renderers';

interface InvoiceDetail
  extends Omit<PurchaseInvoiceFormData, 'invoiceNumber'>, DocumentLineItemsFields {
  id: string;
  companyId: string;
  // Numer faktury dostawcy jest opcjonalny — istniejąca faktura zapisana bez
  // numeru wraca z serwera jako null. Typ formularza pozostaje string (input
  // zawsze kontrolowany), więc przy ładowaniu robimy `?? ''`.
  invoiceNumber: string | null;
  // T18.3.z.C4 — source tracker (manual/symfonia/ksef)
  source: string;
  externalId: string | null;
  externalSource: string | null;
  gcrecord: string | null;
  /** Wiersze dekretu NESTED w odpowiedzi GET (`invoice.decreeLines`, patrz
   * `apps/api/lib/documents/decree.ts` — `fetchDocumentDecreeLines`). Brak osobnego nagłówka
   * (uproszczenie modelu dekretacji, wzorem WF `DocumentAccountingEntry`). */
  decreeLines: DecreeLine[];
  vatRegister: DocumentVatRegisterRow[];
  /** Typ dokumentu w Symfonii FK dla TEGO dokumentu; null = domyślny z mapowania integracji. */
  symfoniaTypeCode: string | null;
  /** Rok obrachunkowy przypisany do dokumentu (z niego dekretowano i do niego pójdzie do FK). */
  symfoniaFiscalYear: string | null;
  /** Data okresu = miesiąc księgowy w Symfonii FK (`YYYY-MM-01`); null = nieustawiona. */
  symfoniaPeriodDate: string | null;
  /** Ślad po wysyłce do Symfonii — FK nie pozwala usunąć wysłanego dokumentu. */
  symfoniaSentAt: string | null;
  symfoniaDocumentNumber: string | null;
  /** Lista wyboru typu + wartość domyślna z mapowania (`invoice.symfoniaType` z GET).
   * Pusta lista opcji = firma bez aktywnej integracji FK → pole ukryte w formatce. */
  symfoniaType?: SymfoniaTypeContext | null;
  /** Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu. */
  extValues?: Record<string, unknown>;
}

function formatAmount(amount: string | null | undefined, currency: string): string {
  if (amount == null) return '';
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency }).format(n);
}

// Pola, które backend POST (Zod POST_BODY_SCHEMA) wymaga jako niepuste przy
// tworzeniu faktury. Walidujemy je po stronie klienta, by podświetlić brakujące
// pole zamiast pokazywać sam toast po odrzuceniu 400.
const REQUIRED_CREATE_FIELDS = [
  'vendorId',
  'issueDate',
  'receivedDate',
  'paymentDueDate',
  'amountNet',
  'amountGross',
] as const satisfies ReadonlyArray<keyof PurchaseInvoiceFormData>;

/** Zwraca klucze wymaganych pól, które są puste w formularzu. */
function collectMissingRequired(form: PurchaseInvoiceFormData): Set<keyof PurchaseInvoiceFormData> {
  const missing = new Set<keyof PurchaseInvoiceFormData>();
  for (const key of REQUIRED_CREATE_FIELDS) {
    const value = form[key];
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      missing.add(key);
    }
  }
  return missing;
}

export function PurchaseInvoiceDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { me } = useTenantAuth();
  const { data: myModules } = useMyModules();
  const processesActive = myModules?.subscriptions.some((s) => s.code === 'processes') ?? false;

  // /api/auth/me zwraca dziś puste `permissions` (wypełnienie odroczone) — pusta lista
  // oznacza „brak wiedzy", nie „brak uprawnień": pokazujemy akcje, backend egzekwuje.
  const permissionsUnknown = !!me && me.permissions.length === 0;

  const canEdit =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.purchase_invoice.edit') === true;

  const canDelete =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.purchase_invoice.delete') === true;

  // Dekretacja — permission dedykowany (zwykle tylko księgowa), niezależny od
  // documents.purchase_invoice.edit (patrz `apps/api/app/api/purchase-invoices/[id]/route.ts`,
  // gałąź `data.decreeLines !== undefined`). Bramkuje TYLKO edytowalność siatki — odczyt
  // (`DecreeLinesSection`) widzi każdy, kto widzi fakturę.
  const canManageAccounting =
    me?.user.isSuperUser === true ||
    permissionsUnknown ||
    me?.permissions.includes('documents.purchase_invoice.manage_accounting') === true;

  const [searchParams, setSearchParams] = useSearchParams();
  // Konwergencja z pilotem (Krok 2): aktywna zakładka ↔ `?tab=` przez wspólny hook.
  const { tab, setTab } = useDocumentDetailTab();

  const [editing, setEditing] = useState(isCreate);
  // Rework D.8 — typ wybrany w edycji, niezapisany do czasu głównego zapisu; reset
  // przy zmianie rekordu (mirror layoutOverrideId w useDocumentTypeAndLayoutParams).
  const [pendingDocumentTypeId, setPendingDocumentTypeId] = useState<string | null>(null);
  useEffect(() => {
    setPendingDocumentTypeId(null);
  }, [id]);
  const [form, setForm] = useState<PurchaseInvoiceFormData>(() => ({
    ...EMPTY_PURCHASE_INVOICE_FORM,
    documentTypeId: isCreate ? (searchParams.get('documentTypeId') ?? null) : null,
  }));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(false);
  // Błędy walidacji per-pole (mapa pole → komunikat) — wspólny mechanizm silnika:
  // niesie i podświetlenie (ramka), i tekst pod polem, i wchłania serwerowy
  // VALIDATION_ERROR. Ujednolicenie z kartotekami (dawniej lokalny `Set` = sama ramka).
  const fe = useFieldErrors();

  const detailQ = useQuery({
    queryKey: ['purchase-invoice', id],
    queryFn: async () => {
      const res = await api.get<{ invoice: InvoiceDetail }>(`/purchase-invoices/${id}`);
      return res.data.invoice;
    },
    enabled: !isCreate && !!id,
  });

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowej faktury — pola
  // pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('purchase_invoices', isCreate);

  // Ext-pola: lokalny stan zasilany wartością z odpowiedzi GET rekordu (`detailQ.data.extValues`,
  // bez osobnego żądania) — pełny kontrakt synchronizacji w nagłówku `useRecordExtValues.ts`;
  // wartości lecą w JEDNYM POST/PATCH (saveAggregate). Przy tworzeniu „wartością serwera” są
  // wartości domyślne pól dodatkowych.
  const { extValues, handleExtChange, markSaved, snapshotEdits, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // Draft edytowalnych pozycji — inicjalizowany z odpowiedzi serwera (efekt niżej),
  // wysyłany jako tablica `items` w tym samym zapisie co dokument. `itemsDirty`
  // steruje semantyką API: wysyłamy `items` TYLKO gdy użytkownik ruszył grid (inaczej
  // pomijamy → backend nie tyka pozycji; bez zbędnego replace-setu przy edycji nagłówka).
  const [itemsDraft, setItemsDraft] = useState<DocumentLineItemDraft[]>([]);
  const [itemsDirty, setItemsDirty] = useState(false);
  // Klucze wierszy niekompletnych z ostatniej próby zapisu (patrz handleSave) —
  // podświetlenie w edytorze, zerowane przy kolejnej udanej walidacji.
  const [invalidItemRowKeys, setInvalidItemRowKeys] = useState<Set<string>>(new Set());
  const handleItemsChange = (next: DocumentLineItemDraft[]) => {
    setItemsDraft(next);
    setItemsDirty(true);
  };

  // Draft edytowalnych wierszy dekretu (Wn/Ma) — mirror itemsDraft/itemsDirty powyżej:
  // inicjalizowany z odpowiedzi serwera (efekt niżej), wysyłany jako `decreeLines` w TYM
  // SAMYM zapisie co dokument. `decreeDirty` steruje semantyką API identycznie jak
  // `itemsDirty` — wysyłamy `decreeLines` TYLKO gdy user ruszył siatkę. Bilans Wn/Ma jest
  // wyłącznie prezentacyjny wewnątrz `DecreeLinesEditor` — zapis zawsze się udaje
  // niezależnie od bilansu (uproszczenie modelu dekretacji, patrz
  // `apps/api/lib/documents/decree.ts`).
  const [decreeDraft, setDecreeDraft] = useState<DecreeLineDraft[]>([]);
  const [decreeDirty, setDecreeDirty] = useState(false);
  const handleDecreeChange = (next: DecreeLineDraft[]) => {
    setDecreeDraft(next);
    setDecreeDirty(true);
  };

  // Typ dokumentu Symfonia (pole formatki dekretacji) — mirror decreeDraft/decreeDirty:
  // null = „domyślny z mapowania integracji", wysyłamy TYLKO gdy user go ruszył.
  const [symfoniaTypeCode, setSymfoniaTypeCode] = useState<string | null>(null);
  const [symfoniaTypeDirty, setSymfoniaTypeDirty] = useState(false);
  const handleSymfoniaTypeChange = (next: string | null) => {
    setSymfoniaTypeCode(next);
    setSymfoniaTypeDirty(true);
  };

  // Rok obrachunkowy dekretu — stan dokumentu, nie tylko wybór w formatce. Osobna flaga
  // (nie `decreeDirty`): edytor raportuje też rok wybrany automatycznie dla dokumentu, który
  // go jeszcze nie ma, żeby zapisał się przy najbliższym zapisie. Flaga steruje WYŁĄCZNIE
  // dołączeniem pola do payloadu — nie udaje, że user coś zmienił.
  const [symfoniaFiscalYear, setSymfoniaFiscalYear] = useState<string | null>(null);
  const [symfoniaFiscalYearDirty, setSymfoniaFiscalYearDirty] = useState(false);
  const handleFiscalYearChange = (next: string) => {
    setSymfoniaFiscalYear(next);
    setSymfoniaFiscalYearDirty(next !== (detailQ.data?.symfoniaFiscalYear ?? null));
  };

  // Draft edytowalnych wierszy RĘCZNEGO rejestru VAT — mirror itemsDraft/itemsDirty
  // powyżej: inicjalizowany z odpowiedzi serwera (efekt niżej), wysyłany jako
  // `vatRegister` w TYM SAMYM zapisie co dokument. `vatRegisterDirty` steruje
  // semantyką API identycznie jak `itemsDirty`/`decreeDirty` — wysyłamy
  // `vatRegister` TYLKO gdy user ruszył siatkę (inaczej pomijamy, backend nie tyka
  // rejestru).
  const [vatRegisterDraft, setVatRegisterDraft] = useState<DocumentVatRegisterRowDraft[]>([]);
  const [vatRegisterDirty, setVatRegisterDirty] = useState(false);
  const handleVatRegisterChange = (next: DocumentVatRegisterRowDraft[]) => {
    setVatRegisterDraft(next);
    setVatRegisterDirty(true);
  };

  // Konwergencja z pilotem (Krok 2): bufor delt tabel dodatkowych — wspólny hook
  // (jeden wpis per tableId, kontrolowany bufor, `getBufferedValue` świeży per render).
  const {
    handleAdditionalDeltasChange,
    getBufferedValue,
    flattenAdditionalDeltas,
    buildAdditionalParentValues,
    resetAdditionalDeltas,
    getAddRowDraft,
    handleAddRowDraftChange,
    resetAddRowDrafts,
  } = useAdditionalTableDeltas();

  // Walidacja klienta przed wysyłką: zbierz błędy per-pole (wymagalność +
  // format sterowany typem pola z katalogu), podświetl je z tekstem pod polem
  // i pokaż toast — nie wysyłaj żądania (serwer i tak odrzuciłby 400).
  // Zwraca, czy zapis wystartował (`false` = zatrzymała go walidacja) — z tego korzysta
  // `useSaveBeforeDecision` (klik decyzji w banerze w edycji = najpierw ten sam zapis).
  const handleSave = (): boolean => {
    const fieldErrors: Record<string, string> = {};

    // Wymagalność — tylko przy tworzeniu (przy edycji PATCH bywa częściowy,
    // ewentualny brak i tak złapie handler onError). Wygrywa nad formatem
    // (pustego pola nie ma sensu walidować formatem — i tak by przeszło).
    const missingRequiredKeys: string[] = [];
    if (isCreate) {
      for (const key of collectMissingRequired(form)) {
        fieldErrors[String(key)] = t('common.validationIssue.required');
        missingRequiredKeys.push(String(key));
      }
    }

    // Format wg typu pola (wspólna oś silnika): e-mail/URL/liczba/data itd. dla
    // pól core i dodatkowych. Puste pola przepuszcza sam walidator.
    const engineErrors = collectEngineFieldErrors(
      purchaseInvoiceEngineConfig,
      form,
      kartotekaFields,
      extValues,
      t,
    );
    for (const [key, msg] of Object.entries(engineErrors)) {
      if (!(key in fieldErrors)) fieldErrors[key] = msg;
    }

    if (Object.keys(fieldErrors).length > 0) {
      fe.clearAll();
      for (const [key, message] of Object.entries(fieldErrors)) fe.set(key, message);
      toast.show({
        variant: 'error',
        title: t('tenant.purchaseInvoices.create.toast.validationError', {
          defaultValue: 'Sprawdź zaznaczone pola formularza',
        }),
        // Etykiety brakujących wymaganych pól (te same nazwy co na formularzu — z katalogu pól).
        description: buildMissingFieldsDescription(
          missingRequiredKeys,
          (key) => resolveFieldLabel(kartotekaFields, key),
          t,
        ),
      });
      return false;
    }
    fe.clearAll();

    // Grid pozycji ruszony — zablokuj zapis, gdy któryś wiersz nie ma kompletu pól
    // wymaganych przez backend (patrz `validateItemsDraft`), zamiast wysyłać
    // niekompletny wiersz i pokazywać dopiero odpowiedź 400 serwera.
    if (itemsDirty && itemsDraft.length > 0) {
      const invalidRows = validateItemsDraft(itemsDraft);
      if (invalidRows.size > 0) {
        setInvalidItemRowKeys(invalidRows);
        toast.show({
          variant: 'error',
          title: t('tenant.documents.items.editor.rowsIncomplete'),
        });
        return false;
      }
    }
    setInvalidItemRowKeys(new Set());

    saveMutation.mutate(form);
    return true;
  };

  // Data okresu (miesiąc księgowy) — stan roboczy żyje TU (hook), nie w `DecreeLinesEditor`:
  // edytor odmontowuje się przy zmianie zakładki, a podpowiedź z daty dokumentu i ręczny
  // wybór muszą to przeżyć. Do payloadu idzie tylko gdy `dirty` (także `null` = wyczyszczenie).
  const symfoniaPeriod = useSymfoniaPeriodDateDraft({
    saved: detailQ.data?.symfoniaPeriodDate,
    documentDate: form.issueDate,
  });

  // Ręczna zmiana Daty okresu gasi błąd serwera z poprzedniej próby zapisu (jak `fe.clear` w
  // onChange innych pól formularza).
  const handlePeriodDateChange = (next: string | null) => {
    fe.clear(PERIOD_DATE_FIELD);
    symfoniaPeriod.change(next);
  };

  // T1.6 (layouty-e1): typ dokumentu do rozstrzygnięcia layoutu — duplikat
  // (walidacja UUID) walidacji `effectiveDocumentTypeId` z
  // `useDocumentTypeAndLayoutParams` niżej — niemożliwe do uniknięcia:
  // ten hook aktywnego layoutu MUSI zostać na stronie i potrzebuje typu PRZED
  // wywołaniem `useDocumentTypeAndLayoutParams` (który z kolei przyjmuje
  // `layoutConfig` STĄD jako `activeLayoutConfig`). Świadomie NIE gate'ujemy
  // hooka poniżej `enabled: false` do czasu załadowania detalu — jeden
  // dodatkowy fetch bez typu (podczas ładowania detalu) + refetch z typem po
  // jego przyjściu, zero ryzyka przedwczesnego renderu fallbacku.
  const rawCreateDocumentTypeIdForLayout = isCreate ? searchParams.get('documentTypeId') : null;
  const documentTypeIdForLayout = isCreate
    ? rawCreateDocumentTypeIdForLayout && UUID_REGEX.test(rawCreateDocumentTypeIdForLayout)
      ? rawCreateDocumentTypeIdForLayout
      : null
    : (pendingDocumentTypeId ?? detailQ.data?.documentTypeId ?? null);

  // F12.N E1.T5b: layout w nowym kształcie + role context dla filtrowania zakładek.
  const { layoutConfig, myRoleId, isSuperUser, previewDockEnabled } = useSystemModuleActiveLayout(
    'purchase_invoices',
    {
      documentTypeId: documentTypeIdForLayout,
    },
  );

  // Etap 2.3 (unifikacja-formatek): create-params / layout-override cluster
  // wyciągnięty do współdzielonego hooka — pełny kontrakt i uzasadnienie
  // (redirect guard, walidacja UUID, reset override przy zmianie rekordu) w
  // nagłówku `useDocumentTypeAndLayoutParams.ts`.
  const {
    createCompanyId,
    missingDocumentTypeId,
    effectiveDocumentTypeId,
    layoutOverrideId,
    setLayoutOverrideId,
    effectiveLayoutConfig,
  } = useDocumentTypeAndLayoutParams({
    isCreate,
    searchParams,
    navigate,
    listRoute: withMenuItem('/purchase-invoices'),
    moduleSlug: 'purchase_invoices',
    recordId: id ?? null,
    detailDocumentTypeId: documentTypeIdForLayout,
    activeLayoutConfig: layoutConfig,
  });

  // Silnik formatek (Krok 2): renderField dla core+ext przez config faktury
  // (nadpisania widgetów = istniejące renderery pi-field-*, delegacja ext do
  // ExtFieldValueInput). `hasError`/`clearError` niosą podświetlenie required
  // (różnica per-config faktur).
  // Gwiazdka „wymagane" przy polu, po którym wybrany typ numeruje (numer nie powstanie
  // bez tej daty). Slug rozwiązany z typu; undefined → brak gwiazdki numeracyjnej.
  const numberingBaseFieldSlug = useNumberingBaseFieldSlug(
    'purchase_invoice',
    effectiveDocumentTypeId,
    createCompanyId ?? detailQ.data?.companyId ?? null,
  );

  // Pola procesu (readonly) na formularzu — instancje rekordu tym samym kluczem query co
  // ProcessApprovalBanner (react-query dedupuje). Bramka `processesActive` jak przy banerze:
  // na tenancie bez modułu Procesy zero requestu (dedup z banerem zachodzi, gdy jest widoczny).
  const processInstancesQuery = useQuery({
    queryKey: recordInstancesKey(DOCUMENTS_MODULE_CODE, id ?? ''),
    queryFn: () => fetchInstancesForRecord(DOCUMENTS_MODULE_CODE, id ?? ''),
    enabled: !!id && processesActive,
    select: selectRecordInstanceRows,
  });
  const processReadonlyExtra = deriveProcessReadonlyExtra(processInstancesQuery.data);

  const { renderField, fields: kartotekaFields } = useKartotekaRenderField(
    purchaseInvoiceEngineConfig,
    {
      form,
      onChange: setForm,
      extValues,
      onExtChange: handleExtChange,
      editable: editing,
      isCreate,
      isFieldExtraRequired: isSlugMatch(numberingBaseFieldSlug),
      // Dokument z pozycjami → kwoty nagłówka wyliczane serwerowo z linii, pola kwot
      // read-only (silnik przelicza w tej samej transakcji przy zapisie). Przed ruszeniem
      // gridu (`!itemsDirty`) czytamy z odpowiedzi serwera, żeby uniknąć jednoklatkowego
      // „flash" enabled przy wczytaniu istniejącej faktury z pozycjami (itemsDraft zapełnia
      // się dopiero w efekcie sync PO pierwszym renderze). Po ruszeniu gridu — z draftu na
      // żywo, żeby dodanie/usunięcie wiersza przełączało kwoty bez czekania na refetch.
      extra: {
        hasItems: (itemsDirty ? itemsDraft.length : (detailQ.data?.items?.length ?? 0)) > 0,
        ...processReadonlyExtra,
      },
      companyId: createCompanyId ?? detailQ.data?.companyId ?? null,
      hasError: (key) => !!fe.get(String(key)),
      getError: (key) => fe.get(String(key)),
      clearError: (key) => fe.clear(String(key)),
    },
  );

  // Top-up (bezwarunkowy, idempotentny) na layoucie EFEKTYWNYM — syntetyzuje
  // zakładkę 'data' z pól core gdy brak aktywnego layoutu. Zamyka też lukę flat-shape.
  // dokument-blokify (2026-08-23): 'ext_fields'/'audit_log' USUNIĘTE z pageTabs —
  // wcześniej top-up dosypywał je bezwarunkowo przy każdym renderze (nawet po świadomym
  // usunięciu przez usera w builderze, bo brak zakładki o danym slugu ≠ brak configu),
  // co łamało portability (usunięcie nie było trwałe). Teraz blok audytu jest zasiany jako
  // NORMALNE dziecko w domyślnym (seedowanym) layoucie `purchase_invoices` — mirror
  // attachments_view/document_items_view, które NIGDY nie były w pageTabs i działają
  // poprawnie (usuwalne, dodawalne z palety, „nic nie ginie"). Pól dodatkowych top-up ani
  // seed nie dokleja — są tylko tam, gdzie stoją w układzie. Self-heal dla pristine
  // layoutów dzieje się przy re-seedzie (PRISTINE_TAB_CHILDREN_WHITELIST), nie w runtime.
  const topUpCtx = useMemo<TopUpContext>(
    () => ({
      pageTabs: [{ slug: 'data', title: t('tenant.documents.shared.tabs.data'), sortOrder: 0 }],
      coreFieldSlugs: purchaseInvoiceEngineConfig.coreFieldSlugs,
    }),
    [t],
  );
  const engineLayout = useMemo(
    () => topUpDocumentLayout(effectiveLayoutConfig, topUpCtx),
    [effectiveLayoutConfig, topUpCtx],
  );

  // Sync server → local form (gdy detail wraca z backendu, gdy refetch po save).
  useEffect(() => {
    if (isCreate) {
      // Wartości domyślne nadpisują wartości startowe z kodu; parametr z adresu — na końcu.
      setForm({
        // Kurs i kwoty formularz trzyma jako tekst.
        ...applyCoreDefaults(EMPTY_PURCHASE_INVOICE_FORM, recordDefaults.core, {
          exchangeRate: numberAsString,
          amountNet: numberAsString,
          amountVat: numberAsString,
          amountGross: numberAsString,
        }),
        documentTypeId: searchParams.get('documentTypeId') ?? null,
      });
      return;
    }
    // Rework D.8 — nie nadpisuj niezapisanych zmian w trakcie edycji: odświeżenie
    // detalu w tle (inna mutacja/zakładka unieważniająca ten klucz) nie może cofnąć
    // edytowanego formularza. Po zapisie efekt dogania świeży stan serwera, bo
    // `editing` wraca na false w tym samym onSuccess co invalidate.
    if (editing) return;
    if (detailQ.data) {
      const d = detailQ.data;
      setForm({
        vendorId: d.vendorId,
        systemNumber: d.systemNumber,
        invoiceNumber: d.invoiceNumber ?? '',
        ksefNumber: d.ksefNumber,
        ksefIssueDate: d.ksefIssueDate ?? null,
        purchaseEvidenceMarker: d.purchaseEvidenceMarker ?? null,
        jpkDocumentMarker: d.jpkDocumentMarker ?? null,
        goodsImport: d.goodsImport ?? false,
        ksefIssueMode: d.ksefIssueMode ?? null,
        documentReceiptChannel: d.documentReceiptChannel ?? null,
        ksefSubmittedAt: d.ksefSubmittedAt ?? null,
        ksefAvailableAt: d.ksefAvailableAt ?? null,
        ksefSourceIp: d.ksefSourceIp ?? null,
        documentType: d.documentType,
        status: d.status,
        urgency: d.urgency,
        correctedInvoiceId: d.correctedInvoiceId,
        issueDate: d.issueDate,
        receivedDate: d.receivedDate,
        saleDate: d.saleDate,
        paymentDueDate: d.paymentDueDate,
        vendorTaxIdCached: d.vendorTaxIdCached,
        vendorNameCached: d.vendorNameCached,
        currencyCode: d.currencyCode,
        exchangeRate: d.exchangeRate,
        amountNet: d.amountNet,
        amountVat: d.amountVat,
        amountGross: d.amountGross,
        paymentMethod: d.paymentMethod,
        paidAt: d.paidAt,
        vendorBankAccountId: d.vendorBankAccountId,
        paymentNotes: d.paymentNotes,
        description: d.description,
        costCenterDefault: d.costCenterDefault,
        rejectionReason: d.rejectionReason,
        notes: d.notes,
        ownerUserId: d.ownerUserId ?? null,
        categoryId: d.categoryId ?? null,
        source: d.source,
        externalId: d.externalId ?? null,
        externalSource: d.externalSource ?? null,
      });
    }

    // documentTypeId czytamy wyłącznie przy pierwszym mount'cie / SPA-nawigacji na /new;
    // dołączenie searchParams powodowałoby reset formularza przy każdej zmianie taba.
    // `recordDefaults.core` zmienia referencję raz (wczytanie) — pola są wtedy jeszcze ukryte.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- searchParams świadomie pominięty (patrz wyżej)
  }, [detailQ.data, isCreate, editing, recordDefaults.core]);

  // Sync server → draft pozycji (przy załadowaniu detalu i po zapisie). Gdy użytkownik
  // ma lokalne zmiany (`itemsDirty`) — draft zostaje nietknięty. Po zapisie onSuccess
  // zeruje `itemsDirty`, więc ten efekt resynchronizuje ze świeżym stanem serwera.
  useEffect(() => {
    if (itemsDirty) return;
    setItemsDraft((detailQ.data?.items ?? []).map(serverItemToDraft));
  }, [detailQ.data?.items, itemsDirty]);

  // Sync server → draft wierszy dekretu (mirror efektu pozycji powyżej). Gdy
  // użytkownik ma lokalne zmiany (`decreeDirty`) — draft zostaje nietknięty. Po
  // zapisie onSuccess zeruje `decreeDirty`, więc ten efekt resynchronizuje ze
  // świeżym stanem serwera.
  useEffect(() => {
    if (decreeDirty) return;
    setDecreeDraft((detailQ.data?.decreeLines ?? []).map(serverDecreeLineToDraft));
  }, [detailQ.data?.decreeLines, decreeDirty]);

  // Sync server → typ dokumentu Symfonia (mirror efektu dekretu powyżej).
  useEffect(() => {
    if (symfoniaTypeDirty) return;
    setSymfoniaTypeCode(detailQ.data?.symfoniaTypeCode ?? null);
  }, [detailQ.data?.symfoniaTypeCode, symfoniaTypeDirty]);

  // Sync server → rok obrachunkowy dokumentu (mirror efektu powyżej).
  useEffect(() => {
    if (symfoniaFiscalYearDirty) return;
    setSymfoniaFiscalYear(detailQ.data?.symfoniaFiscalYear ?? null);
  }, [detailQ.data?.symfoniaFiscalYear, symfoniaFiscalYearDirty]);

  // Sync server → draft rejestru VAT (mirror efektu pozycji powyżej). Gdy użytkownik
  // ma lokalne zmiany (`vatRegisterDirty`) — draft zostaje nietknięty. Po zapisie
  // onSuccess zeruje `vatRegisterDirty`, więc ten efekt resynchronizuje ze świeżym
  // stanem serwera.
  useEffect(() => {
    if (vatRegisterDirty) return;
    setVatRegisterDraft((detailQ.data?.vatRegister ?? []).map(serverVatRegisterRowToDraft));
  }, [detailQ.data?.vatRegister, vatRegisterDirty]);

  // FIX CRITICAL: kwoty nagłówka są disabled+puste, gdy dokument ma pozycje (patrz
  // `hasItems` przekazane do `useKartotekaRenderField` niżej) — bez tego efektu formularz
  // wysyłałby `amountNet`/`amountGross` puste, a POST/PATCH tego wymaga (nawet gdy serwer
  // i tak przeliczy je z linii w tej samej transakcji). Synchronizujemy TYLKO gdy user
  // ruszył grid (`itemsDirty`) — przy pierwszym wczytaniu istniejącej faktury formularz ma
  // już DOKŁADNE kwoty z serwera i nie wolno ich nadpisać przybliżonym podglądem klienta.
  useEffect(() => {
    if (!itemsDirty || itemsDraft.length === 0) return;
    const totals = computeTotalsPreview(itemsDraft);
    setForm((f) => ({
      ...f,
      amountNet: String(totals.valueNet),
      amountVat: String(totals.valueVat),
      amountGross: String(totals.valueGross),
    }));
  }, [itemsDraft, itemsDirty]);

  // Klik decyzji w banerze procesu, gdy formularz jest w edycji: najpierw ten sam zapis co
  // „Zapisz", okno decyzji dopiero po jego sukcesie (useSaveBeforeDecision; „zapis już trwa"
  // sprawdza sam hak po kluczu `saveMutationKey` mutacji zapisu tej kopii formularza).
  // `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError` i z anulowania
  // ostrzeżenia kontroli niżej.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: id,
    save: handleSave,
  });

  // Przycisk SQL (klocek `sql_button`): zadania w tle na tej fakturze — stan, pasek blokady, renderer
  // klocka, utworzenie → zadanie → przejście (patrz nagłówek `useDocumentSqlButton`).
  const sqlButton = useDocumentSqlButton({
    recordClass: 'purchase_invoice',
    isCreate,
    recordId: id,
    layoutConfig: effectiveLayoutConfig,
    save: handleSave,
    createCompanyId,
    saveBeforeDecision,
  });

  // Kontrola dokumentu (F20+ reużywalny hook, patrz useControlWarning) — `mutate`
  // celowo owinięty w lambdę: `saveMutation` deklarowana niżej, ale to wywołanie
  // trafia dopiero do zamknięcia `handleConfirm`, uruchamianego po pełnym mount'cie.
  const controlWarning = useControlWarning<
    PurchaseInvoiceFormData & { warningsAccepted?: boolean }
  >({
    mutate: (payload) => saveMutation.mutate(payload),
    // Anulowane ostrzeżenie kontroli = zapis się nie odbył → okno decyzji się nie otwiera, a zadanie
    // przycisku SQL kliknięte na nowej fakturze nie rusza.
    onCancel: sqlButton.onSaveAborted,
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: PurchaseInvoiceFormData & { warningsAccepted?: boolean }) => {
      // Backend POST/PATCH wymaga payloadu bez pustych stringów (null preferowany).
      const cleaned: Record<string, unknown> = {
        vendorId: data.vendorId,
        invoiceNumber: data.invoiceNumber?.trim() || null,
        ksefNumber: data.ksefNumber || null,
        ksefIssueDate: data.ksefIssueDate || null,
        purchaseEvidenceMarker: data.purchaseEvidenceMarker || null,
        jpkDocumentMarker: data.jpkDocumentMarker || null,
        goodsImport: data.goodsImport,
        ksefIssueMode: data.ksefIssueMode || null,
        documentReceiptChannel: data.documentReceiptChannel || null,
        documentType: data.documentType,
        status: data.status,
        urgency: data.urgency,
        correctedInvoiceId: data.correctedInvoiceId,
        issueDate: data.issueDate,
        receivedDate: data.receivedDate,
        saleDate: data.saleDate,
        paymentDueDate: data.paymentDueDate,
        vendorTaxIdCached: data.vendorTaxIdCached,
        vendorNameCached: data.vendorNameCached,
        currencyCode: data.currencyCode,
        exchangeRate: data.exchangeRate || null,
        amountNet: data.amountNet,
        amountVat: data.amountVat || null,
        amountGross: data.amountGross,
        paymentMethod: data.paymentMethod || null,
        paidAt: data.paidAt,
        vendorBankAccountId: data.vendorBankAccountId,
        paymentNotes: data.paymentNotes,
        description: data.description,
        costCenterDefault: data.costCenterDefault,
        rejectionReason: data.rejectionReason,
        notes: data.notes,
        ownerUserId: data.ownerUserId,
        categoryId: data.categoryId,
      };

      // Rework D.8 — typ dokumentu wchodzi do payloadu przy tworzeniu ZAWSZE, a przy
      // edycji TYLKO gdy user faktycznie wybrał inny typ (pendingDocumentTypeId). Bez
      // warunku edycja wysyłałaby dziś `null` (schema PATCH: documentTypeId optional,
      // NIE nullable) i dostawała 400.
      if (isCreate) {
        cleaned.documentTypeId = data.documentTypeId || null;
      } else if (
        pendingDocumentTypeId !== null &&
        pendingDocumentTypeId !== detailQ.data?.documentTypeId
      ) {
        cleaned.documentTypeId = pendingDocumentTypeId;
      }

      // Kontrola dokumentu (tenant hook `document_control`) — ostrzeżenie miękkie
      // ponawiane z tą flagą po potwierdzeniu w dialogu (patrz useControlWarning).
      if (data.warningsAccepted) {
        cleaned.warningsAccepted = true;
      }

      // Krok 5 atomic-save refactor: jeden zapis — extValues + delty tabel
      // dodatkowych jadą w TYM SAMYM body co pola faktury (backend zapisuje
      // wszystko w jednej transakcji, patrz saveAggregate). Pola opcjonalne —
      // pomijamy gdy puste, zachowanie identyczne jak dotąd dla callerów bez nich.
      // Migawka edycji tuż przed włożeniem `extValues` do żądania — PRZED oczekiwaniem na
      // definicje pól tabel (`buildAdditionalParentValues`): edycja zrobiona w tym czasie nie
      // trafia do body, więc `markSaved()` w onSuccess jej nie zdejmie (nagłówek `useRecordExtValues.ts`).
      snapshotEdits();
      if (Object.keys(extValues).length > 0) {
        cleaned.extValues = extValues;
      }
      const additionalTablesBody = flattenAdditionalDeltas();
      if (additionalTablesBody) {
        cleaned.additionalTables = additionalTablesBody;
        // Wartości pól faktury z ekranu dla wiązań `parent_field` w wierszach tabel dodatkowych
        // — OBOK delt, liczone ze stanu przekazanego do zapisu (`data`), tylko skalary.
        const additionalParentValues = await buildAdditionalParentValues(
          buildAdditionalViewRecordValues(
            extValues,
            buildCoreFieldRecordValues(purchaseInvoiceEngineConfig, data),
          ),
          'purchase_invoice',
        );
        if (additionalParentValues) {
          cleaned.additionalParentValues = additionalParentValues;
        }
      }

      // Increment 2: pozycje jadą w TYM SAMYM body (replace-set; kwoty pochodne i
      // amount_* przelicza silnik serwera w tej samej transakcji). Wysyłamy TYLKO gdy
      // grid ruszony — inaczej pomijamy `items` (backend nie tyka pozycji). Pusty draft
      // po ruszeniu = `[]` = kasacja pozycji + powrót do trybu kwot ręcznych.
      if (itemsDirty) {
        cleaned.items = itemsDraft.map(draftToPayload);
      }

      // Dekretacja — mirror `items` powyżej: wysyłamy `decreeLines` TYLKO gdy user
      // ruszył siatkę Wn/Ma (`decreeDirty`), inaczej pomijamy (backend nie tyka
      // wierszy). Pusty draft po ruszeniu = `[]` = kasacja wszystkich wierszy.
      if (decreeDirty) {
        cleaned.decreeLines = decreeDraft.map(decreeDraftLineToPayload);
      }

      // Typ dokumentu Symfonia — mirror `decreeLines`: wysyłamy tylko po zmianie w
      // formatce; `null` przywraca wartość domyślną z mapowania integracji.
      if (symfoniaTypeDirty) {
        cleaned.symfoniaTypeCode = symfoniaTypeCode;
      }

      // Rok obrachunkowy dekretu — dołączamy, gdy różni się od zapisanego (także gdy dokument
      // dostał go automatycznie z najnowszego planu kont).
      if (symfoniaFiscalYearDirty && symfoniaFiscalYear) {
        cleaned.symfoniaFiscalYear = symfoniaFiscalYear;
      }

      // Data okresu — gdy różni się od zapisanej (podpowiedź z daty dokumentu, ręczny wybór
      // albo wyczyszczenie → `null`).
      // Tylko przy edycji: przy tworzeniu pole jest ukryte, a POST go nie przyjmuje — gdyby szkic
      // kiedyś stał się „zmieniony" już przy tworzeniu, klucz i tak nie trafi do żądania.
      if (!isCreate && symfoniaPeriod.dirty) {
        cleaned.symfoniaPeriodDate = symfoniaPeriod.draft;
      }

      // Rejestr VAT — mirror `items`/`decreeLines` powyżej: wysyłamy `vatRegister` TYLKO
      // gdy user ruszył siatkę (`vatRegisterDirty`), inaczej pomijamy (backend nie
      // tyka rejestru). Pusty draft po ruszeniu = `[]` = kasacja wszystkich wierszy.
      if (vatRegisterDirty) {
        cleaned.vatRegister = vatRegisterDraft.map(vatRegisterDraftToPayload);
      }

      let recordId: string;
      let processWarning: ProcessWarning | undefined;
      if (isCreate) {
        // CORE INVARIANT: w trybie „Wszystkie firmy" interceptor NIE wysyła
        // X-Company-Id (brak aktywnej firmy) — bez jawnego nagłówka backend
        // (`requireCompany`) odrzuciłby POST z MISSING_HEADER, nawet gdy
        // documentTypeId jednoznacznie wskazuje firmę. Jawny nagłówek per-request
        // wygrywa z interceptorem (patrz `lib/api.ts` — nie nadpisuje już ustawionego
        // X-Company-Id) i działa identycznie w trybie jednej aktywnej firmy.
        const res = await api.post<{
          invoice: { id: string; systemNumber: string | null };
          // Ostrzeżenie startu automatycznego trasa odsyła OBOK `invoice`, nie w jej środku.
          processWarning?: ProcessWarning;
        }>(
          '/purchase-invoices',
          cleaned,
          createCompanyId ? { headers: { 'X-Company-Id': createCompanyId } } : undefined,
        );
        recordId = res.data.invoice.id;
        processWarning = res.data.processWarning;
      } else {
        await api.patch(`/purchase-invoices/${id}`, cleaned);
        recordId = id!;
      }

      return { id: recordId, processWarning };
    },
    onSuccess: async (data) => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      queryClient.invalidateQueries({ queryKey: ['purchase-invoices'] });
      // Zapis mógł zmienić typ dokumentu (a od niego zależą prawa obiegu): flagi „Wyślij do
      // akceptacji" / „Anuluj proces" odświeżamy RAZEM z rekordem (bez czekania na nie).
      void invalidateRecordInstances(queryClient, DOCUMENTS_MODULE_CODE, data.id);
      // Awaitowane: bez tego setItemsDirty(false)/setEditing(false) (niżej) flipowały
      // stan PRZED odświeżeniem detalu, co na chwilę pokazywało stare pozycje przed
      // resyncem draftu (post-save stale-items flash). Reszta invalidacji zostaje
      // fire-and-forget — tylko ta jedna gatekeeperuje UI po zapisie pozycji.
      // Pola dodatkowe zapisane razem z dokumentem — od tej chwili wartość serwera (z odświeżonego
      // detalu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      await queryClient.invalidateQueries({ queryKey: ['purchase-invoice', data.id] });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'purchase_invoices'] });
      // Krok 5 atomic-save refactor: po zapisie odśwież rekordy tabel dodatkowych
      // (prefix match — łapie wszystkie ['additional-records', tableId, parentId])
      // i wyczyść lokalny bufor delt — backend już je zapisał atomowo.
      queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      resetAdditionalDeltas();
      // Finding [G] (pre-commit-verify): szkic niezatwierdzonego add-row też czyścimy
      // po udanym zapisie — spójnie z buforem zatwierdzonych wierszy powyżej.
      resetAddRowDrafts();
      // Pozycje zapisane atomowo — zeruj dirty, żeby efekt sync odświeżył draft ze
      // świeżego stanu serwera (przeliczone kwoty/pary walutowe) po refetchu detalu.
      setItemsDirty(false);
      // Dekret zapisany atomowo (mirror pozycji powyżej) — zeruj dirty, efekt sync
      // odświeży draft ze świeżego stanu serwera.
      setDecreeDirty(false);
      // Typ dokumentu Symfonia zapisany (mirror dekretu) — zeruj dirty, efekt sync
      // odświeży pole ze świeżego stanu serwera.
      setSymfoniaTypeDirty(false);
      setSymfoniaFiscalYearDirty(false);
      symfoniaPeriod.reset();
      // Rejestr VAT zapisany atomowo (mirror pozycji/dekretu powyżej) — zeruj dirty,
      // efekt sync odświeży draft ze świeżego stanu serwera.
      setVatRegisterDirty(false);
      if (isCreate) {
        toast.show({
          variant: 'success',
          title: t('tenant.purchaseInvoices.toast.created', { defaultValue: 'Faktura zapisana' }),
        });
        if (data.processWarning) {
          toast.show({
            variant: 'warning',
            title: processWarningMessage(t, data.processWarning),
          });
        }
        // Przycisk SQL kliknięty na nowej fakturze: zadanie rusza TERAZ — z id nowej faktury, PRZED
        // przejściem pod jej adres. Porażka zlecenia dostaje własny komunikat; faktura już istnieje,
        // więc przejście i tak następuje.
        await sqlButton.runPendingCreateRun(data.id);
        // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) —
        // reset do podglądu, bo create→detail nie remontuje strony.
        setEditing(false);
        navigate(withMenuItem(`/purchase-invoices/${data.id}`), { state: { replaceTab: true } });
        return;
      }
      toast.show({
        variant: 'success',
        title: t('tenant.purchaseInvoices.toast.updated', {
          defaultValue: 'Faktura zaktualizowana',
        }),
      });
      setPendingDocumentTypeId(null);
      setEditing(false);
      // Zapis wywołany kliknięciem decyzji w banerze → teraz otwórz okno decyzji (no-op, gdy
      // zapis poszedł zwykłym „Zapisz"). Tylko gałąź edycji — przy tworzeniu nie ma banera.
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      // Zapis wywołany kliknięciem decyzji w banerze: każdy błąd poza ostrzeżeniem kontroli
      // kończy sprawę (okno decyzji się nie otworzy). O `CONTROL_WARNING` rozstrzyga user w
      // dialogu: potwierdzenie ponawia zapis (okno otworzy jego sukces), anulowanie woła
      // `onCancel`. Na samym początku, żeby żadna późniejsza gałąź (ani rzut) nie ominęła tego.
      // To samo dla zadania przycisku SQL klikniętego na nowej fakturze: utworzenie się nie udało → nie rusza.
      if (code !== 'CONTROL_WARNING') sqlButton.onSaveAborted();
      // Podświetlenie pól odrzuconych przez serwer (ramka + tekst pod polem) musi
      // zobaczyć KAŻDĄ odpowiedź, zanim jakakolwiek gałąź zrobi wczesny `return` —
      // inaczej stare podświetlenie z poprzedniej próby zostaje przy innym błędzie
      // (patrz JSDoc `useFieldErrors.ts`). Komunikaty po polsku z issues Zoda.
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        // Kolejność: (1) polski komunikat z issue Zoda (wymagane/format), (2) własny
        // komunikat serwera z `details` — reguły krzyżowe dat/kwot NIE mają `issues`,
        // tylko gotowe polskie zdanie w `details` (np. „Termin płatności musi być >=
        // data otrzymania"); bez tego fallbacku ginął pod generykiem. (3) generyk.
        // Angielskie default-y Zoda z `details` (np. „Invalid input") nie przeciekają:
        // pola z issue mają wpis w `fieldMsgs`, więc wygrywają zanim dojdzie do messages[0].
        // Data okresu: komunikat serwera jest konkretny („1. dniem miesiąca", „Rok poza
        // zakresem Symfonii FK") — wygrywa z ogólnym „zły format" z issue Zoda.
        translate: (field, messages) =>
          field === PERIOD_DATE_FIELD
            ? (messages[0] ?? fieldMsgs[field] ?? t('common.validationIssue.generic'))
            : (fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic')),
      });
      // Kontrola dokumentu (CONTROL_ERROR/CONTROL_WARNING/CONTROL_HOOK_FAILED) ma
      // pierwszeństwo — jeśli obsłużona (toast lub dialog już pokazany), reszta gałęzi
      // (specyficzne dla faktur zakupu) nie musi się odpalać.
      if (controlWarning.handleError(err, form)) return;
      // Zapis odrzucony, bo na fakturze trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „nie udało się zapisać”; odświeżony stan zadań pokaże pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      if (code === 'INVOICE_NUMBER_TAKEN') {
        toast.show({
          variant: 'error',
          title: t('tenant.purchaseInvoices.create.toast.duplicate', {
            defaultValue: 'Faktura o tym numerze już istnieje',
            number: '?',
          }),
        });
        return;
      }
      if (code === 'VENDOR_NOT_FOUND') {
        toast.show({
          variant: 'error',
          title: t('tenant.purchaseInvoices.create.toast.vendorNotFound', {
            defaultValue: 'Dostawca nie istnieje',
          }),
        });
        return;
      }
      // Zakładka otwarta przed deployem uproszczenia modelu dekretacji nadal wysyła stary
      // klucz `decree` — backend go jawnie odrzuca (lib/documents/decree.ts,
      // rejectLegacyDecreeField) zamiast cicho ignorować, żeby wiersze dekretu nie
      // przepadły bez informacji.
      if (code === 'CLIENT_OUTDATED_RELOAD_REQUIRED') {
        toast.show({
          variant: 'error',
          title: t('tenant.purchaseInvoices.create.toast.outdatedReload', {
            defaultValue: 'Wersja aplikacji się zmieniła — odśwież stronę i spróbuj ponownie',
          }),
        });
        return;
      }
      // Odrzucona część słownikowa zapisu (pozycja spoza słownika / brak lub zła wartość
      // parametru słownika) — czytelna przyczyna; przed ogólną gałęzią 400 poniżej.
      const dictionaryMsg = mapDictionarySaveError(err, t);
      if (dictionaryMsg !== null) {
        toast.show({ variant: 'error', title: dictionaryMsg });
        return;
      }
      if (isAxiosError(err) && err.response?.status === 400) {
        // Pola odrzucone przez serwer są już podświetlone z tekstem pod polem
        // (fe.applyServerError na górze handlera); toast dokłada zwięzły opis
        // pierwszego pola.
        // Błąd tylko Daty okresu → konkretny komunikat serwera w toaście; inaczej zwykły opis.
        const specific =
          periodDateValidationToast(err, t) ??
          buildValidationToastMessage(
            err,
            (field) =>
              field === PERIOD_DATE_FIELD
                ? t('tenant.documents.decree.editor.periodDateLabel')
                : resolveFieldLabel(kartotekaFields, field),
            t,
          );
        toast.show({
          variant: 'error',
          title:
            specific ??
            t('tenant.purchaseInvoices.create.toast.validationError', {
              defaultValue: 'Sprawdź wymagane pola',
            }),
          // Brak wymaganego pola dodatkowego serwer zgłasza w `details` (bez `issues`), więc
          // `specific` jest wtedy puste — nazwij brakujące pola komunikatem serwera.
          description: specific ? undefined : buildExtFieldServerMessages(err, kartotekaFields),
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.purchaseInvoices.create.toast.genericError', {
          defaultValue: 'Nie udało się zapisać faktury',
        }),
      });
    },
  });

  // Kontrola dokumentu (F20+ reużywalny hook) dla flow DELETE — osobne klucze i18n
  // ("Usuń mimo to") niż flow SAVE, patrz useControlWarning.
  const deleteControlWarning = useControlWarning<{ warningsAccepted?: boolean }>({
    mutate: (payload) => deleteMutation.mutate(payload),
    titleKey: 'tenant.documents.controlWarning.deleteTitle',
    confirmLabelKey: 'tenant.documents.controlWarning.deleteConfirmLabel',
  });

  const deleteMutation = useMutation({
    mutationFn: async (variables: { warningsAccepted?: boolean } = {}) => {
      const query = variables.warningsAccepted ? '?warningsAccepted=true' : '';
      await api.delete(`/purchase-invoices/${id}${query}`);
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.purchaseInvoices.toast.deleted'),
      });
      queryClient.invalidateQueries({ queryKey: ['purchase-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['distinct-values', 'purchase_invoices'] });
      navigate(withMenuItem('/purchase-invoices'));
    },
    onError: (err, variables) => {
      if (deleteControlWarning.handleError(err, variables ?? {})) return;
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.purchaseInvoices.toast.deleteError'),
      });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async () => {
      await api.post(`/purchase-invoices/${id}/restore`, {});
    },
    onSuccess: () => {
      toast.show({
        variant: 'success',
        title: t('tenant.purchaseInvoices.toast.restored'),
      });
      void queryClient.invalidateQueries({ queryKey: ['purchase-invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['purchase-invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['distinct-values', 'purchase_invoices'] });
    },
    onError: (err) => {
      // Przywrócenie odrzucone na czas zadania przycisku SQL — opis zadania + pasek blokady.
      if (sqlButton.handleLockedError(err)) return;
      toast.show({
        variant: 'error',
        title: t('tenant.purchaseInvoices.toast.restoreError'),
      });
    },
  });

  function handleCancel() {
    fe.clearAll();
    setPendingDocumentTypeId(null);
    symfoniaPeriod.reset();
    if (isCreate) {
      navigate(withMenuItem('/purchase-invoices'));
      return;
    }
    // Resync form z server'a (cofnij niezapisane zmiany)
    if (detailQ.data) {
      const d = detailQ.data;
      setForm({
        vendorId: d.vendorId,
        systemNumber: d.systemNumber,
        invoiceNumber: d.invoiceNumber ?? '',
        ksefNumber: d.ksefNumber,
        ksefIssueDate: d.ksefIssueDate ?? null,
        purchaseEvidenceMarker: d.purchaseEvidenceMarker ?? null,
        jpkDocumentMarker: d.jpkDocumentMarker ?? null,
        goodsImport: d.goodsImport ?? false,
        ksefIssueMode: d.ksefIssueMode ?? null,
        documentReceiptChannel: d.documentReceiptChannel ?? null,
        ksefSubmittedAt: d.ksefSubmittedAt ?? null,
        ksefAvailableAt: d.ksefAvailableAt ?? null,
        ksefSourceIp: d.ksefSourceIp ?? null,
        documentType: d.documentType,
        status: d.status,
        urgency: d.urgency,
        correctedInvoiceId: d.correctedInvoiceId,
        issueDate: d.issueDate,
        receivedDate: d.receivedDate,
        saleDate: d.saleDate,
        paymentDueDate: d.paymentDueDate,
        vendorTaxIdCached: d.vendorTaxIdCached,
        vendorNameCached: d.vendorNameCached,
        currencyCode: d.currencyCode,
        exchangeRate: d.exchangeRate,
        amountNet: d.amountNet,
        amountVat: d.amountVat,
        amountGross: d.amountGross,
        paymentMethod: d.paymentMethod,
        paidAt: d.paidAt,
        vendorBankAccountId: d.vendorBankAccountId,
        paymentNotes: d.paymentNotes,
        description: d.description,
        costCenterDefault: d.costCenterDefault,
        rejectionReason: d.rejectionReason,
        notes: d.notes,
        ownerUserId: d.ownerUserId ?? null,
        categoryId: d.categoryId ?? null,
        source: d.source,
        externalId: d.externalId ?? null,
        externalSource: d.externalSource ?? null,
      });
    }
    discardEdits();
    // Anuluj porzuca też zbuforowane zmiany tabel dodatkowych i szkice add-row (nie tylko zapis).
    resetAdditionalDeltas();
    resetAddRowDrafts();
    setEditing(false);
  }

  // Redirect w toku (obsługiwany w useDocumentTypeAndLayoutParams) — nie renderuj formularza create
  // ani na moment, żeby user nie zobaczył migawki formularza bez typu dokumentu.
  if (missingDocumentTypeId) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="pi-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="pi-detail-loading">
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div className="space-y-4 p-6">
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden="true" />}
          onClick={() => navigate(withMenuItem('/purchase-invoices'))}
        >
          {t('tenant.purchaseInvoices.detail.backToList')}
        </Button>
        <div className="rounded-xl border border-status-error-border bg-status-error-bg p-6 text-sm text-status-error">
          {t('tenant.purchaseInvoices.detail.notFound')}
        </div>
      </div>
    );
  }

  if (isCreate && recordDefaults.status !== 'ready') {
    return <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="pi-detail" />;
  }

  const recordId = isCreate ? null : (id ?? null);
  const dockCompanyId = createCompanyId ?? detailQ.data?.companyId ?? undefined;

  return (
    <ModuleGuard moduleCode="crm_core">
      <DocumentPreviewDockHost
        enabled={previewDockEnabled && !!recordId}
        moduleId="purchase_invoices"
        ownerType="document"
        ownerId={recordId}
        companyId={dockCompanyId}
      >
        <div className="space-y-4 p-6" data-testid="pi-detail-page">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <IconButton
                onClick={() => navigate(withMenuItem('/purchase-invoices'))}
                data-testid="pi-detail-back"
                aria-label={t('common.back')}
              >
                <ArrowLeft />
              </IconButton>
              <div className="flex items-start gap-3">
                <Receipt className="mt-1 h-6 w-6 text-text-secondary" aria-hidden="true" />
                <div>
                  {isCreate ? (
                    <h1
                      className="text-2xl font-semibold text-text-primary"
                      data-testid="pi-detail-create-title"
                    >
                      {t('tenant.purchaseInvoices.create.title')}
                      <DocumentTypeCreateBadge
                        docClass="purchase_invoice"
                        documentTypeId={effectiveDocumentTypeId}
                        companyId={createCompanyId}
                        testId="pi-detail-create-type-badge"
                        onChange={(newTypeId, newCompanyId) => {
                          const params = new URLSearchParams(searchParams);
                          params.set('documentTypeId', newTypeId);
                          params.set('companyId', newCompanyId);
                          setSearchParams(params, { replace: true });
                          setForm((prev) => ({ ...prev, documentTypeId: newTypeId }));
                        }}
                      />
                    </h1>
                  ) : (
                    <>
                      <h1
                        className="text-2xl font-semibold text-text-primary"
                        data-testid="pi-detail-title"
                      >
                        {form.systemNumber ?? form.invoiceNumber}
                        {canEdit && (
                          <DocumentTypeChangeBadge
                            docClass="purchase_invoice"
                            documentTypeId={
                              pendingDocumentTypeId ?? detailQ.data?.documentTypeId ?? null
                            }
                            companyId={detailQ.data?.companyId ?? null}
                            testId="pi-detail-change-type-badge"
                            disabled={!editing}
                            onConfirm={(newTypeId) => {
                              setPendingDocumentTypeId(newTypeId);
                              setLayoutOverrideId(null);
                            }}
                          />
                        )}
                        {pendingDocumentTypeId &&
                          pendingDocumentTypeId !== detailQ.data?.documentTypeId && (
                            <span
                              className="ml-2 rounded bg-accent-50 px-2 py-0.5 align-middle text-xs font-normal text-accent-700"
                              data-testid="pi-detail-pending-type-number-hint"
                            >
                              {t('tenant.documents.typePicker.numberPendingOnSave')}
                            </span>
                          )}
                      </h1>
                      <div className="mt-1 flex items-center gap-3 text-xs text-text-tertiary">
                        {form.status && (
                          <span className="rounded bg-accent-50 px-2 py-0.5 text-accent-700">
                            {invoiceStatusLabel(form.status, t)}
                          </span>
                        )}
                        <span>{form.vendorNameCached ?? '—'}</span>
                        {form.amountGross && (
                          <span className="tabular-nums">
                            {formatAmount(form.amountGross, form.currencyCode)}
                          </span>
                        )}
                        {/* T18.3.z.UI1 — badge źródła faktury (Manual / Symfonia / KSeF) */}
                        {detailQ.data?.source && <SourceBadge source={detailQ.data.source} />}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <DocumentLayoutPicker
                moduleSlug="purchase_invoices"
                value={layoutOverrideId}
                onChange={setLayoutOverrideId}
                myRoleId={myRoleId}
                isSuperUser={isSuperUser}
              />
              {editing ? (
                <>
                  <Button
                    onClick={handleCancel}
                    variant="secondary"
                    leftIcon={<XIcon size={14} aria-hidden="true" />}
                    disabled={saveMutation.isPending}
                    data-testid="pi-detail-cancel-btn"
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    onClick={handleSave}
                    variant="primary"
                    leftIcon={
                      isCreate ? (
                        <Plus size={14} aria-hidden="true" />
                      ) : (
                        <Save size={14} aria-hidden="true" />
                      )
                    }
                    loading={saveMutation.isPending}
                    disabled={saveMutation.isPending}
                    data-testid="pi-detail-save-btn"
                  >
                    {isCreate ? t('common.create') : t('common.save')}
                  </Button>
                </>
              ) : detailQ.data?.gcrecord ? (
                canDelete && (
                  <Button
                    onClick={() => setConfirmRestore(true)}
                    variant="primary"
                    leftIcon={<RotateCcw size={14} aria-hidden="true" />}
                    loading={restoreMutation.isPending}
                    disabled={restoreMutation.isPending}
                    data-testid="pi-detail-restore-btn"
                  >
                    {t('tenant.purchaseInvoices.actions.restore')}
                  </Button>
                )
              ) : (
                <>
                  {canDelete && (
                    <Button
                      onClick={() => setConfirmDelete(true)}
                      variant="danger"
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Trash2 size={14} aria-hidden="true" />}
                      data-testid="pi-detail-delete-btn"
                    >
                      {t('common.delete')}
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      onClick={() => setEditing(true)}
                      disabled={sqlButton.recordLocked}
                      leftIcon={<Pencil size={14} aria-hidden="true" />}
                      data-testid="pi-detail-edit-btn"
                    >
                      {t('common.edit')}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Zadanie przycisku SQL na fakturze (w kolejce lub w toku): faktury nie da się zapisać ani
            usunąć, „Edytuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania. */}
          {sqlButton.lockBanner}

          {/* Process approval banner */}
          {!isCreate && id && processesActive ? (
            <ProcessApprovalBanner
              moduleCode={DOCUMENTS_MODULE_CODE}
              recordId={id}
              documentTypeId={detailQ.data?.documentTypeId ?? null}
              beforeDecision={saveBeforeDecision.beforeDecision}
            />
          ) : null}

          {/* Silnik formatek (Krok 2): jedna ścieżka create/edit/view — descent
          RecursiveFormRenderer na layoucie po top-upie (syntetyzuje 'data' z pól
          core gdy brak aktywnego layoutu; zakładka `data` schodzi w children i
          woła renderField per liść — core przez config faktury, ext przez
          ExtFieldValueInput). Pole dodatkowe pokazuje się tylko tam, gdzie ktoś
          postawił je w układzie (top-up niczego nie dokleja). 'lines' (Pozycje) renderuje element
          document_items_view przez children (ADR-0076, supersesja subcollection);
          'accounting'/'documents' zostają placeholderami (tabRenderers, do T16.9/10);
          Typ dokumentu: badge nagłówka (create) + numeracja per typ (LOCKED) — poza
          silnikiem.
          dokument-blokify (2026-08-23): 'audit_log' nie jest już zaszytą zakładką
          (tabRenderers) — to przenośny blok (purchase_invoice_audit_view) dispatchowany
          przez contextRenderers, usuwalny/dodawalny z palety jak 'lines'/'attachments'.
          Samodzielnej zakładki „Pola dodatkowe" (purchase_invoice_ext_fields_view) już nie ma. */}
          <RecursiveFormRenderer
            layoutConfig={engineLayout}
            activeTabId={tab}
            onTabChange={setTab}
            myRoleId={myRoleId}
            isSuperUser={isSuperUser}
            renderField={renderField}
            tabRenderers={{
              // Zakładka „Pozycje" (slug 'lines') renderuje teraz element
              // document_items_view przez children (patrz contextRenderers) — stary
              // placeholder-override usunięty, bo tabRenderers[slug] ma precedencję nad
              // children i przesłaniałby grid pozycji (supersesja subcollection, ADR-0076).
              // Zakładka „Dekretacja" (slug 'accounting') analogicznie renderuje teraz
              // element decree_lines_view przez children (patrz contextRenderers) — stary
              // placeholder-override USUNIĘTY (miałby precedencję nad children i
              // przesłoniłby siatkę Wn/Ma).
              documents: () => (
                <div
                  className="rounded-xl border border-dashed border-border-secondary bg-bg-secondary p-12 text-center"
                  data-testid="pi-detail-placeholder-documents"
                >
                  <p className="mx-auto max-w-2xl text-sm text-text-secondary">
                    {t('tenant.purchaseInvoices.detail.placeholder.documents')}
                  </p>
                </div>
              ),
            }}
            contextRenderers={{
              ...buildDocumentAdditionalRenderers(recordId, {
                editable: editing,
                buffered: true,
                processesActive,
                beforeDecision: saveBeforeDecision.beforeDecision,
                documentTypeId: detailQ.data?.documentTypeId ?? null,
                onDeltasChange: handleAdditionalDeltasChange,
                getAddRowDraft,
                onAddRowDraftChange: handleAddRowDraftChange,
                // Kontrolowany bufor — `getBufferedValue` (ze wspólnego hooka) czyta
                // BIEŻĄCĄ deltę per render, więc remount zakładki (Tabs) nie gubi wiersza.
                getBufferedValue,
                companyId: createCompanyId ?? detailQ.data?.companyId ?? null,
                // F22 (widoki dodatkowe) — recordValues dla wiązania `record_field`: ext (żywe,
                // działa w create) + core przez resolver silnika formatek.
                extValues,
                coreRecordValues: buildCoreFieldRecordValues(purchaseInvoiceEngineConfig, form),
                // Klucz wiązań `parent_field` w tabelach dodatkowych tej klasy dokumentu.
                parentKey: 'purchase_invoice',
              }),
              // Klocek „Przycisk SQL": zadanie w tle na tej fakturze. Prawo = ten sam sygnał co „Edytuj”
              // (usunięta faktura nie ma „Edytuj”, więc i przycisk jest wyszarzony); serwer i tak sprawdza
              // prawo edycji z typem i etapem procesu.
              sql_button: sqlButton.renderSqlButton({
                canEdit: canEdit && !detailQ.data?.gcrecord,
                savePending: saveMutation.isPending,
              }),
              // dokument-blokify (2026-08-23): przenośny blok „Audit log" — mirror kartotek
              // (EntityAuditViewElements). Guard `!isCreate && recordId` (nie samo `id`) — w
              // trybie create `id==='new'`. Samodzielnej zakładki „Pola dodatkowe" już nie ma:
              // pole dodatkowe pokazuje się tylko tam, gdzie ktoś postawił je w układzie, a jego
              // wartość jest czytana z odpowiedzi GET rekordu.
              purchase_invoice_audit_view: () =>
                !isCreate && recordId ? (
                  <SystemModuleAuditTab
                    moduleSlug="purchase_invoices"
                    recordId={recordId}
                    testIdPrefix="pi-audit"
                  />
                ) : (
                  <SaveFirstNotice message={t('tenant.purchaseInvoices.detail.tabDisabled.save')} />
                ),
              document_items_view: ({ element }) =>
                editing ? (
                  <DocumentItemsEditor
                    value={itemsDraft}
                    onChange={handleItemsChange}
                    currencyCode={form.currencyCode}
                    companyId={createCompanyId ?? detailQ.data?.companyId ?? null}
                    invalidRowKeys={invalidItemRowKeys}
                    columnSettings={element.settings}
                  />
                ) : (
                  <DocumentItemsSection
                    items={detailQ.data?.items}
                    vatBreakdown={detailQ.data?.vatBreakdown}
                    amountNet={detailQ.data?.amountNet}
                    amountVat={detailQ.data?.amountVat}
                    amountGross={detailQ.data?.amountGross}
                    currencyCode={detailQ.data?.currencyCode}
                    columnSettings={element.settings}
                  />
                ),
              // Zakładka „Dekretacja" — siatka Wn/Ma. Edytowalna TYLKO gdy strona jest w
              // trybie edycji i user ma permission dedykowany (`canManageAccounting` —
              // patrz backend `documents.<klasa faktury>.manage_accounting`, niezależny od
              // `documents.purchase_invoice.edit`). Brak blokady po zatwierdzeniu (uproszczenie
              // modelu dekretacji — wróci później przez uprawnienia, patrz
              // `apps/api/lib/documents/decree.ts`).
              decree_lines_view: () =>
                editing && canManageAccounting ? (
                  <div className="space-y-3">
                    <DecreeLinesEditor
                      value={decreeDraft}
                      onChange={handleDecreeChange}
                      companyId={createCompanyId ?? detailQ.data?.companyId ?? null}
                      symfoniaType={detailQ.data?.symfoniaType ?? null}
                      symfoniaTypeCode={symfoniaTypeCode}
                      onSymfoniaTypeCodeChange={handleSymfoniaTypeChange}
                      fiscalYear={symfoniaFiscalYear}
                      onFiscalYearChange={handleFiscalYearChange}
                      periodDate={symfoniaPeriod.draft}
                      onPeriodDateChange={handlePeriodDateChange}
                      onPeriodDateActivate={symfoniaPeriod.activate}
                      periodDateError={fe.get(PERIOD_DATE_FIELD)}
                      toolbar={
                        <>
                          <DecreeSuggestControls
                            documentId={recordId}
                            companyId={createCompanyId ?? detailQ.data?.companyId ?? null}
                            documentClass="purchase_invoice"
                            currentLines={decreeDraft}
                            onApplySuggestion={handleDecreeChange}
                            data-testid="pi-decree-suggest-controls"
                          />
                          <DecreeProcedureRunControls
                            documentId={recordId}
                            companyId={createCompanyId ?? detailQ.data?.companyId ?? null}
                            documentClass="purchase_invoice"
                            onApply={handleDecreeChange}
                            onApplyVatRegister={handleVatRegisterChange}
                            data-testid="pi-decree-procedure-run-controls"
                          />
                        </>
                      }
                      headerAside={
                        detailQ.data?.symfoniaSentAt ? (
                          <SymfoniaSentStatus
                            sentAt={detailQ.data.symfoniaSentAt}
                            sentDocumentNumber={detailQ.data.symfoniaDocumentNumber}
                            data-testid="pi-send-to-symfonia-sent-badge"
                          />
                        ) : null
                      }
                      data-testid="pi-decree-lines-editor"
                    />
                    {detailQ.data?.symfoniaType?.options?.length ? (
                      <SendToSymfoniaButton
                        documentId={recordId}
                        sentAt={detailQ.data?.symfoniaSentAt}
                        sentDocumentNumber={detailQ.data?.symfoniaDocumentNumber}
                        showSentStatus={false}
                        data-testid="pi-send-to-symfonia"
                      />
                    ) : null}
                  </div>
                ) : (
                  <DecreeLinesSection
                    lines={detailQ.data?.decreeLines}
                    symfoniaTypeCode={detailQ.data?.symfoniaTypeCode}
                    symfoniaTypeDefaultCode={detailQ.data?.symfoniaType?.defaultCode}
                    fiscalYear={detailQ.data?.symfoniaFiscalYear}
                    periodDate={detailQ.data?.symfoniaPeriodDate}
                    symfoniaIntegrationActive={!!detailQ.data?.symfoniaType?.options?.length}
                    documentDate={detailQ.data?.issueDate}
                    actions={
                      detailQ.data?.symfoniaType?.options?.length ? (
                        <SendToSymfoniaButton
                          documentId={recordId}
                          sentAt={detailQ.data?.symfoniaSentAt}
                          sentDocumentNumber={detailQ.data?.symfoniaDocumentNumber}
                          data-testid="pi-send-to-symfonia-ro"
                        />
                      ) : null
                    }
                  />
                ),
              // Zakładka rejestru VAT (ręczny, ADR poza silnikiem dekretu/pozycji) —
              // warunek PROSTSZY niż dekret: brak stanu „locked" i brak permission
              // dedykowanego (backend jedzie pod `documents.purchase_invoice.edit`, patrz
              // `apps/api/app/api/purchase-invoices/[id]/route.ts` — pole `vatRegister`).
              // `amountNet` przekazany z ŻYWEGO stanu formularza (`form.amountNet`), NIE
              // ze zrzutu serwera (`detailQ.data?.amountNet`) — ten ostatni jest
              // nieaktualny zaraz po edycji kwoty nagłówka, zanim zapis dotrze do serwera.
              vat_register_view: ({ element }) =>
                editing && canEdit ? (
                  <DocumentVatRegisterEditor
                    value={vatRegisterDraft}
                    onChange={handleVatRegisterChange}
                    registerDictionaryCode="vat_registers_purchase"
                    amountNet={form.amountNet}
                    editable
                    columnSettings={element.settings}
                    data-testid="pi-vat-register-editor"
                  />
                ) : (
                  <DocumentVatRegisterSection
                    rows={detailQ.data?.vatRegister}
                    registerDictionaryCode="vat_registers_purchase"
                    columnSettings={element.settings}
                  />
                ),
              // Załączniki wymagają realnego recordId (dokument fizyczny w MinIO
              // podpięty pod document.id) — niedostępne przed pierwszym zapisem,
              // mirror audit_log wyżej (contextRenderers).
              attachments_view: () =>
                recordId ? (
                  <InvoiceAttachmentsSection
                    documentId={recordId}
                    companyId={createCompanyId ?? detailQ.data?.companyId ?? undefined}
                  />
                ) : null,
              ...buildDocumentPreviewContextRenderer({
                ownerType: 'document',
                ownerId: recordId,
                companyId: createCompanyId ?? detailQ.data?.companyId ?? undefined,
                extValues,
              }),
            }}
          />

          <ConfirmDialog
            open={confirmDelete}
            title={t('common.confirmDelete')}
            message={t('tenant.purchaseInvoices.deleteConfirm', {
              number: form.systemNumber ?? form.invoiceNumber,
            })}
            confirmLabel={t('common.delete')}
            variant="danger"
            onConfirm={() => {
              setConfirmDelete(false);
              deleteMutation.mutate({});
            }}
            onClose={() => setConfirmDelete(false)}
          />

          <ConfirmDialog
            open={confirmRestore}
            title={t('tenant.purchaseInvoices.restoreConfirm', {
              number: form.systemNumber ?? form.invoiceNumber,
            })}
            message={t('tenant.purchaseInvoices.restoreConfirmBody')}
            confirmLabel={t('tenant.purchaseInvoices.actions.restore')}
            variant="primary"
            onConfirm={() => {
              setConfirmRestore(false);
              restoreMutation.mutate();
            }}
            onClose={() => setConfirmRestore(false)}
          />

          {controlWarning.dialog}
          {deleteControlWarning.dialog}
        </div>
      </DocumentPreviewDockHost>
    </ModuleGuard>
  );
}
